import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import SearchBar from 'shared/components/search-bar/search-bar';
import { FilterChipOption } from 'shared/components/table-filter-chips/multi-value-filter-chip/models/filter-chip-option';
import MultiValueFilterChip from 'shared/components/table-filter-chips/multi-value-filter-chip/multi-value-filter-chip';
import OrganisationUnitFilterChip from 'shared/components/table-filter-chips/organisation-unit-filter-chip/organisation-unit-filter-chip';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';
import { OrganisationUnitTreeProvider } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

import { useGatewaysContextProvider } from 'features/gateway/context/gateway-provider';
import { FilterValueType } from 'features/gateway/domain/models/gateway-filter-value';
import { GatewayType } from 'features/gateway/domain/models/gateway-type';
import GatewayMonitoringFilter from 'features/gateway/views/components/gateway-monitoring-filter';

import './gateway-filters.scss';

export default function GatewayFilters(): ReactElement {
  const { t } = useTranslation('gateway');

  const { refresh, gatewayFilterHook } = useGatewaysContextProvider();

  const getTypeFilterValues = (): FilterChipOption[] =>
    Object.entries(GatewayType)
      .filter(([_, value]) => value !== GatewayType.Unknown)
      .map(([key, value]) => ({
        key,
        value: [key],
        label: t(`filterValues.type.${value}`),
      }));

  return (
    <div className="gateway-filters-container">
      <div className="top-row">
        <div className="gateway-filters">
          <SearchBar onSearchValueChanged={gatewayFilterHook.submitSearch} searchValue={gatewayFilterHook.searchBarValue} />
          <MultiValueFilterChip
            options={getTypeFilterValues()}
            onOptionsSelected={(keys: string[]) => gatewayFilterHook.selectOption(FilterValueType.Type, keys)}
            placeHolder={t('filters.type')}
            selectedOptions={getTypeFilterValues().filter((x) =>
              gatewayFilterHook.activeFilters.find((x) => x.filterValueType === FilterValueType.Type)?.values.includes(x.key),
            )}
          />
          <OrganisationUnitTreeProvider.All>
            <OrganisationUnitFilterChip
              onOptionsSelected={(keys: SelectedOrganisationTreeNode[]) => {
                gatewayFilterHook.changeSelectedOrganisationUnits(keys);
                gatewayFilterHook.selectOption(
                  FilterValueType.OrganisationUnit,
                  keys.map((x) => x.id),
                );
              }}
              selectedOptions={gatewayFilterHook.selectedOrganisationUnits}
              placeHolder={t('filters.organisationUnits')}
            />
          </OrganisationUnitTreeProvider.All>

          <div className="clear-filters">
            <Button variant="text" color="primary" onClick={gatewayFilterHook.clearFilters}>
              {t('filters.clearFilters')}
            </Button>
          </div>
        </div>

        {refresh && (
          <IconButton className="refresh-button" onClick={refresh}>
            <RefreshRounded />
          </IconButton>
        )}
      </div>

      {gatewayFilterHook.monitorFiltersIsSuccess && gatewayFilterHook.monitorFilters.length > 0 && (
        <div className="monitoring-filters">
          <GatewayMonitoringFilter.Offline />
          <GatewayMonitoringFilter.ServiceOffline />
          <GatewayMonitoringFilter.PassiveOffline />
          <GatewayMonitoringFilter.Online />
        </div>
      )}
    </div>
  );
}
